<ion-header>
  <ion-toolbar color="primary" class="custom-toolbar">
    <!-- Level Indicator with both buttons -->
    <div class="level-indicator" slot="primary">
      <div class="level-glow"></div>
      <div class="level-text">
        <div class="help-button" (click)="showKeyboardHelp()">
          <ion-icon name="keypad-outline"></ion-icon>
          <span>{{ 'Keyboard Help' | translate }}</span>
        </div>
        <div class="divider"></div>
        <div class="help-button" routerLink="/type">
          <ion-icon name="list-outline"></ion-icon>
          <span>{{ 'Keyboard Type' | translate }}</span>
        </div>
      </div>
    </div>

    <ion-button slot="start" size="small" routerLink="/"><ion-icon name="book-outline"></ion-icon> {{ 'Learn' | translate }}</ion-button>
    <ion-button 
      slot="end" 
      size="small" 
      (click)="toggleHandOverlay()" 
      *ngIf="windowWidth > 1024">          
      <ion-icon [name]="showHandOverlay ? 'hand-right' : 'hand-right-outline'"></ion-icon>
      {{ 'Hand Guide' | translate }}
    </ion-button>

  </ion-toolbar>
</ion-header>

<ion-content [style]="contentStyle">
  <!-- Info Section -->
<div *ngIf="showManual" class="info-section">
  <div class="user-manual">
    <div class="sticky-close">
      <ion-button (click)="closeManual()" color="light" fill="clear">
        <ion-icon name="close-circle" slot="icon-only"></ion-icon>
      </ion-button>
    </div>

    <ion-card>
      <ion-card-header>
        <ion-card-title>Tamil Typing Practice Guide</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <p>Welcome to the Tamil typing practice system. This guide will help you learn touch typing in Tamil using your physical keyboard.</p>

        <h2>1. Keyboard Layout</h2>
        <div class="content-block">
          <div class="guide-image">
            <img src="assets/images/tamil.my-typing-keyboard.png" alt="Tamil Typing Keyboard Layout">
          </div>
          <h3>Key Mapping:</h3>
          <ul>
            <li><strong>Left Hand (Vowels - Uyir):</strong> Q through V keys</li>
            <li><strong>Right Hand (Consonants - Mei):</strong> Y through / keys</li>
            <li><strong>Home Position:</strong> Rest fingers on ASDF - JKL;</li>
          </ul>
        </div>

        <h2>2. Practice Interface</h2>
        <div class="content-block">
          <ul>
            <li><strong>Letter Preview:</strong> Shows upcoming characters</li>
            <li><strong>Hand Display:</strong> Shows finger positions</li>
            <li><strong>Audio Feedback:</strong>
              <ul>
                <li>Correct keystroke: Click sound</li>
                <li>Incorrect keystroke: Error sound</li>
              </ul>
            </li>
            <li><strong>Visual Feedback:</strong>
              <ul>
                <li>Green checkmark: Completed letters</li>
                <li>Highlighted: Current target</li>
                <li>Red: Incorrect keystroke</li>
              </ul>
            </li>
          </ul>
        </div>

        <h2>3. Learning Path</h2>
        <div class="content-block">
          <ul>
            <li><strong>Progressive Learning:</strong>
              <ul>
                <li>Start with home row keys</li>
                <li>Add top row keys</li>
                <li>Add bottom row keys</li>
                <li>Practice combined characters</li>
              </ul>
            </li>
            <li><strong>Performance Tracking:</strong>
              <ul>
                <li>Words Per Minute (WPM)</li>
                <li>Accuracy Percentage</li>
                <li>Error Count</li>
                <li>Progress Bar</li>
              </ul>
            </li>
          </ul>
        </div>

        <h2>4. Tips for Success</h2>
        <div class="content-block">
          <ul>
            <li>Focus on accuracy before speed</li>
            <li>Practice regularly in short sessions</li>
            <li>Watch the hand display for proper technique</li>
            <li>Complete each lesson before moving to the next</li>
          </ul>
        </div>

        <div class="footer-note">
          <p>Tamil UyirMei Keyboard Layout developed by Muhelen Murugan</p>
        </div>
      </ion-card-content>
    </ion-card>
  </div>
</div>

  <!-- Metrics Bar -->
  <div class="metrics-bar">
    <div class="metric">
      <div class="metric-value">{{ metrics.wpm }}</div>
      <div class="metric-label">{{ 'WPM' | translate }}</div>
    </div>
    <div class="metric">
      <div class="metric-value">{{ metrics.accuracy }}%</div>
      <div class="metric-label">{{ 'Accuracy' | translate }}</div>
    </div>
    <div class="metric">
      <div class="metric-value">{{ metrics.errors }}</div>
      <div class="metric-label">{{ 'Errors' | translate }}</div>
    </div>
    <div class="progress-wrapper">
      <div class="progress-bar" [style.width.%]="metrics.progress"></div>
    </div>
  </div>

  <!-- Main Typing Area -->
  <div class="typing-container">
    <!-- Letter Preview Row -->
    <div class="letter-preview-row">
      <div class="letter-box" *ngFor="let char of getNextCharacters(); let i = index"
           [class.completed]="i < currentLetterIndex"
           [class.current]="i === currentLetterIndex"
           [class.incorrect]="i === currentLetterIndex && isIncorrect">
        <div class="checkmark" *ngIf="i < currentLetterIndex">✓</div>
        <div class="letter-display">{{ combineUyirMei(char) }}</div>
      </div>
    </div>

    <!-- Hand overlay -->
    <app-hand-overlay 
      *ngIf="showHandOverlay && !isLessonComplete"
      [currentKey]="currentKey"
      [keyboardLayout]="keyboardLayout">
    </app-hand-overlay>

    <!-- Keyboard Container -->
    <div class="keyboard-container">
      <!-- Top Row -->
      <div class="keyboard-row">
        <!-- First 4 keys are Uyir keys -->
        <div class="key" *ngFor="let key of getUyirKeys('top')" 
             [class.active]="!isWaitingForUyir && currentKey === key.tamil"
             [class.uyirmei-active]="isWaitingForUyir && key.tamil === expectedChar"
             [class.incorrect]="(incorrectKey === key.tamil) || (incorrectKey === key.english)"
             (click)="onKeyPress(key.tamil)"
             (touchstart)="onKeyPress(key.tamil); $event.preventDefault()">
          <span class="key-hint">{{key.english}}</span>
          <span class="tamil-key">{{key.tamil}}</span>
        </div>
        <!-- Remaining keys are Mei keys -->
        <div class="key" *ngFor="let key of keyboardLayout.top.slice(4)" 
             [class.active]="currentKey === key.tamil"
             [class.mei-selected]="isWaitingForUyir && selectedMei?.tamil === key.tamil"
             [class.incorrect]="(incorrectKey === key.tamil) || (incorrectKey === key.english)"
             (click)="onMeiKeyPress(key)"
             (touchstart)="onMeiKeyPress(key); $event.preventDefault()">
          <span class="key-hint">{{key.english}}</span>
          <span class="tamil-key">{{key.tamil}}</span>
        </div>
      </div>

      <!-- Home Row -->
      <div class="keyboard-row">
        <!-- First 4 keys are Uyir keys -->
        <div class="key home-key" *ngFor="let key of getUyirKeys('middle')" 
             [class.active]="currentKey === key.tamil"
             [class.incorrect]="(incorrectKey === key.tamil) || (incorrectKey === key.english)"
             (click)="onKeyPress(key.tamil)"
             (touchstart)="onKeyPress(key.tamil); $event.preventDefault()">
          <span class="key-hint">{{key.english}}</span>
          <span class="tamil-key">{{key.tamil}}</span>
        </div>
        <!-- Remaining keys are Mei keys -->
        <div class="key" *ngFor="let key of keyboardLayout.middle.slice(4)" 
             [class.active]="currentKey === key.tamil"
             [class.incorrect]="(incorrectKey === key.tamil) || (incorrectKey === key.english)"
             (click)="onMeiKeyPress(key)"
             (touchstart)="onMeiKeyPress(key); $event.preventDefault()">
          <span class="key-hint">{{key.english}}</span>
          <span class="tamil-key">{{key.tamil}}</span>
        </div>
      </div>

      <!-- Bottom Row -->
      <div class="keyboard-row">
        <!-- First 4 keys are Uyir keys -->
        <div class="key" *ngFor="let key of getUyirKeys('bottom')" 
             [class.active]="currentKey === key.tamil"
             [class.incorrect]="(incorrectKey === key.tamil) || (incorrectKey === key.english)"
             (click)="onKeyPress(key.tamil)"
             (touchstart)="onKeyPress(key.tamil); $event.preventDefault()">
          <span class="key-hint">{{key.english}}</span>
          <span class="tamil-key">{{key.tamil}}</span>
        </div>
        <!-- Remaining keys are Mei keys -->
        <div class="key" *ngFor="let key of keyboardLayout.bottom.slice(4)" 
             [class.active]="currentKey === key.tamil"
             [class.incorrect]="(incorrectKey === key.tamil) || (incorrectKey === key.english)"
             (click)="onMeiKeyPress(key)"
             (touchstart)="onMeiKeyPress(key); $event.preventDefault()">
          <span class="key-hint">{{key.english}}</span>
          <span class="tamil-key">{{key.tamil}}</span>
        </div>
      </div>

      <!-- Space Bar -->
      <div class="keyboard-row">
        <div class="key space"
             [class.active]="currentKey === ' '"
             [class.incorrect]="incorrectKey === ' '"
             (click)="onKeyPress(' ')"
             (touchstart)="onKeyPress(' '); $event.preventDefault()">
          <span class="key-hint">Space</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Lesson Complete Dialog -->
  <div class="lesson-complete-overlay" *ngIf="isLessonComplete">
    <div class="lesson-complete-card">
      <div class="complete-header">
        <ion-icon name="checkmark-circle"></ion-icon>
        <h2>{{ 'Completed' | translate }}!</h2>
      </div>
      <div class="complete-stats">
        <div class="stat-item">
          <div class="stat-value">{{ metrics.wpm }}</div>
          <div class="stat-label">{{ 'WPM' | translate }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ metrics.accuracy }}%</div>
          <div class="stat-label">{{ 'Accuracy' | translate }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ metrics.errors }}</div>
          <div class="stat-label">{{ 'Errors' | translate }}</div>
        </div>
      </div>
      <div class="complete-actions">
        <ion-button fill="outline" (click)="resetLesson()">
          <ion-icon name="refresh-outline" slot="start"></ion-icon>
          {{ 'Redo' | translate }}
        </ion-button>
        <ion-button (click)="nextLesson()">
          <ion-icon name="arrow-forward-outline" slot="end"></ion-icon>
          {{ 'Next' | translate }}
        </ion-button>
      </div>
    </div>
  </div>
</ion-content>

<ion-footer class="ion-no-border">
  <div class="ion-text-center footer-style">
    <span>© 2025 TAMIL.MY · <a href="https://otpam.com" target="_blank" class="footer-link">OTPAM SDN BHD (1446394-V).</a> All Rights Reserved.</span>
  </div>     
</ion-footer>